<script lang="ts">
    import {Page, Block, Button, Chip, f7} from 'framework7-svelte';
    import { onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import { navparts } from '../utils/navparts';
    import ActionBar from '../components/ActionBar.svelte';
    import Header from '../components/Header.svelte';
    import {
        getOldLisaProjectList, getOldLisaProjectMap, getOldLisaProjectMeasurementTimestamp,
    } from "../api/imports.api";
    import {importTokenStore} from "../stores/import-token.store";
    import type {ImportProject} from "../types/imports/import-project.type";
    import {getProjects} from "../api/projects.api";
    import {notify} from "../utils/notify";
    import {
        importProjectAndMeasurementsFromOldLisa,
    } from "../helpers/import-projects.helper";
    import type {ImportProjectMap} from "../types/imports/import-project-map.type";

    let importProjectItems: ImportProject[] = [];
    let existingProjectNames: string[] = [];
    let importing = false;
    
    onMount(async () => {
        if($importTokenStore == null){
            return;
        }
        
        navparts.set([]);
        await setProjects()
    });
    
    async function setProjects() {
        const existingProjects =  await getProjects();
        existingProjectNames = existingProjects.map(x => x.name);
        importProjectItems = await getOldLisaProjectList($importTokenStore);
    }
    
    async function showImportProjectPopUp(importableProject: ImportProject) {
        f7.preloader.show();
        let legacyProjectMap = null;
        let measurementCount = null;
        
        try {
            measurementCount = await getOldLisaProjectMeasurementTimestamp($importTokenStore, importableProject.Id);
            legacyProjectMap = await getOldLisaProjectMap($importTokenStore, importableProject.Id);
        }
        catch(error) {
            notify($_("pages.imports.PREPARE_IMPORT_FAILED"));
            console.error(
                $_("pages.imports.ERROR_FETCHING_IMPORT_PROJECT", { values: { name: importableProject.Name }})
                + '\n' + error
            );
        }
        f7.preloader.hide();
        
        const name = importableProject.Name;
        const numberOfGates = legacyProjectMap.Gates.length;
        const numberOfRoutes = legacyProjectMap.Routes.length;
        
        const sectionCount = legacyProjectMap.Routes.flatMap(route => route.Sections).length;
        const potentialMeasurementCount = measurementCount * sectionCount;
        
        // Adding in some empty lines to make the message more readable
        const message = [
            $_("pages.imports.IMPORT_PROJECT_POPUP_1", { values: { name }}), '',
            $_("pages.imports.IMPORT_PROJECT_POPUP_2", { values: { 
                numberOfGates: formatNumber(numberOfGates), numberOfRoutes: formatNumber(numberOfRoutes) }
            }), '',
            $_("pages.imports.IMPORT_PROJECT_POPUP_3", { values: { 
                sectionCount: formatNumber(sectionCount), measurementCount: formatNumber(measurementCount) }
            }), '',
            $_("pages.imports.IMPORT_PROJECT_POPUP_4", { values: {
                sectionMeasurementCount: formatNumber(potentialMeasurementCount) }
            }), '',
            $_("pages.imports.IMPORT_PROJECT_POPUP_5")
        ].join('<br>');
        
        f7.dialog.confirm(
            message,
            '',
            async () => {
                importing = true;
                f7.preloader.show();
                
                try {
                    await importProject(importableProject, legacyProjectMap, potentialMeasurementCount);
                }
                catch(error) {
                    console.error((
                        $_("pages.imports.ERROR_IMPORTING_PROJECT", { values: { name: importableProject.Name }})
                        + '\n' + error
                    ));
                    
                    f7.popup.close();
                    f7.preloader.hide();
                    importing = false;
                    
                    notify($_("pages.imports.IMPORT_FAILED", { values: { name: importableProject.Name }}));
                }
                
                await setProjects();
                
                f7.popup.close();
                f7.preloader.hide();
                importing = false;

                notify($_("pages.imports.IMPORT_SUCCESS", { values: { name: importableProject.Name }}));
            }
        );
    }
    
    async function importProject(importableProject: ImportProject, legacyProjectMap: ImportProjectMap, measurementCount: number): Promise<void> {
        await importProjectAndMeasurementsFromOldLisa($importTokenStore, importableProject, legacyProjectMap, measurementCount);
    }
    
    function formatNumber(number: number): string {
        return number.toLocaleString('nl-NL');
    }
</script>

<Page>
    <Header />

    <ActionBar>
        {#if $importTokenStore !== null}
            <Button outline class="display-inline-flex padding-horizontal" loginScreenOpen="#login-screen" iconMaterial="add">
                {$_("pages.imports.LOG_IN_AGAIN")}
            </Button>
        {/if}
    </ActionBar>

    <Block>
        {#if importing}
            <div>
                <span class="centered-span">
                    {$_("pages.imports.IMPORTING_PROJECT_TEXT_1")}
                </span>
                <span class="centered-span">
                    {$_("pages.imports.IMPORTING_PROJECT_TEXT_2")}
                </span>
            </div>
        {:else}
            {#if $importTokenStore == null}
                <div>
                    <span class="centered-span">
                        {$_("pages.imports.NOT_LOGGED_IN")}
                    </span>
                </div>
            {/if}
            {#if importProjectItems.length > 0}
                <div class="card data-table">
                    <table>
                        <thead>
                        <tr>
                            <th style="width: 20em">
                                <span class="table-head-label">
                                    {$_("shared.HEADER_IMPORT")}
                                </span>
                            </th>
                            <th style="width: 30em">
                                <span class="table-head-label">
                                    {$_("shared.HEADER_NAME")}
                                </span>
                            </th>
                            <th style="width: 20em">
                                <span class="table-head-label">
                                    {$_("shared.HEADER_MEASUREMENTS")}
                                </span>
                            </th>
                            <th style="width: 30em">
                                <span class="table-head-label">
                                    {$_("shared.HEADER_PROJECT_WITH_NAME_EXISTS")}
                                </span>
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {#each importProjectItems as item, i}
                            <tr
                                    class="table-row"
                            >
                                <td>
                                    <Button on:click={() => {showImportProjectPopUp(item)}}>
                                        {$_("pages.imports.IMPORT_PROJECT")}
                                    </Button>
                                </td>
                                <td>{item.Name}</td>
                                <td>
                                    {#if Object.values(item.Recordings).filter((interval) => interval !== undefined && interval !== null && interval !== 0).length > 0}
                                        <Chip text={$_("pages.imports.ACTIVE_MEASUREMENTS")} mediaBgColor="green" class="margin-right" />
                                    {:else}
                                        <span>
                                            {$_("pages.imports.NO_ACTIVE_MEASUREMENTS")}
                                        </span>
                                    {/if}
                                </td>
                                <td>
                                    {#if existingProjectNames.includes(item.Name)}
                                        <Chip text={$_("pages.imports.PROJECT_NAME_ALREADY_EXISTS")} mediaBgColor="green" class="margin-right" />
                                    {:else}
                                        {$_("pages.imports.PROJECT_NAME_DOES_NOT_EXIST")}
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
        {/if}
    </Block>
</Page>

<style lang="scss">
    .table-row {
        cursor: pointer;
    }
    .table-sub-label {
      float: right;
    }

    .centered-span {
      display: table;
      margin: 0 auto;
    }
</style>
